import React, { useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from 'components/ui/dialog';
import { useAuth } from 'contexts/AuthContext';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { formatPrice, formatDateTime } from 'lib/format';
import { Plus, ArrowDown, ArrowUp, Landmark, FileText } from 'lucide-react';
import { useLanguage } from 'lib/i18n';

export default function WalletPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [txs, setTxs] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(50);
  const [busy, setBusy] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [connectError, setConnectError] = useState('');
  const [connectBusy, setConnectBusy] = useState(false);
  const [retryingWithdrawal, setRetryingWithdrawal] = useState(false);
  const [retryingEarningId, setRetryingEarningId] = useState('');
  const [withdrawalError, setWithdrawalError] = useState('');
  const [receiptUrl, setReceiptUrl] = useState('');
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [receiptError, setReceiptError] = useState('');

  useEffect(() => () => {
    if (receiptUrl) window.URL.revokeObjectURL(receiptUrl);
  }, [receiptUrl]);
  const bankPayoutMethods = ['stripe_connect', 'stripe_connect_balance'];
  const pendingWithdrawal = txs.find(tx => tx.type === 'payout' && bankPayoutMethods.includes(tx.method) && tx.status === 'pending');
  const reservedAmount = connectStatus?.reserved_withdrawal_amount ?? pendingWithdrawal?.amount ?? 0;
  const withdrawalState = connectStatus?.withdrawal_state || (pendingWithdrawal
    ? pendingWithdrawal.stripe_payout_id ? 'bank_pending'
      : pendingWithdrawal.method === 'stripe_connect_balance' ? 'queued' : 'awaiting_confirmation'
    : 'none');
  const stripeAvailable = Math.max(0, Number(connectStatus?.connected_available_balance ?? 0));
  const withdrawableBalance = connectStatus?.withdrawal_pending ? 0
    : Math.max(0, Math.min(stripeAvailable, Number(connectStatus?.withdrawable_balance ?? 0)));
  const canWithdraw = !balanceLoading && connectStatus?.ready && !connectStatus?.withdrawal_pending && withdrawableBalance >= 1;
  const canRetryWithdrawal = !balanceLoading && connectStatus?.ready && connectStatus?.withdrawal_retryable
    && reservedAmount >= 1 && stripeAvailable >= reservedAmount;

  const refreshWallet = async () => {
    await Promise.allSettled([refreshUser(), loadConnectStatus()]);
    await load();
  };

  const payoutLabel = (transaction) => {
    if (transaction.status === 'completed') return t('wallet.payoutPaid');
    if (transaction.status === 'failed') return transaction.method === 'stripe_connect_balance'
      ? t('wallet.connectPayoutFailed') : t('wallet.payoutFailed');
    if (transaction.method === 'stripe_connect_balance' && !transaction.stripe_payout_id) return t('wallet.payoutQueued');
    return transaction.stripe_payout_id ? t('wallet.payoutBankPending') : t('wallet.payoutUnconfirmed');
  };

  const load = () => api.get('/transactions').then(r => setTxs(r.data));
  const loadConnectStatus = async () => {
    setBalanceLoading(true);
    try {
      const response = await api.get('/transactions/connect/status');
      setConnectStatus(response.data);
      setConnectError('');
    } catch (err) {
      setConnectStatus(null);
      setConnectError(getApiErrorMessage(err, t('wallet.connectSetupError')));
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    const refreshQueuedWithdrawal = () => {
      const initialLoad = load();
      return loadConnectStatus().then(() => Promise.allSettled([initialLoad, load()]));
    };
    const timer = window.setInterval(refreshQueuedWithdrawal, 60_000);
    window.addEventListener('focus', refreshQueuedWithdrawal);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshQueuedWithdrawal);
    };
  }, []);

  useEffect(() => {
    load();
    loadConnectStatus().then(load);
    const url = new URL(window.location.href);
    const connectReturn = url.searchParams.get('connect');
    if (connectReturn === 'refresh' || connectReturn === 'return') {
      // Consume the flag before requesting another single-use link to avoid loops.
      url.searchParams.delete('connect');
      window.history.replaceState(window.history.state, '', url.toString());
      if (connectReturn === 'refresh') configureStripePayout(true);
      else refreshUser();
    }
  }, []);

  const topup = async () => {
    try {
      const response = await api.post('/transactions/topup', { amount: Number(amount), type: 'topup', method: 'card' });
      if (response.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }
      toast.error(t('wallet.topupUnavailable'));
    } catch (err) { toast.error(getApiErrorMessage(err, t('wallet.networkError'))); }
  };

  const configureStripePayout = async (forceOnboarding = false) => {
    setConnectBusy(true);
    setConnectError('');
    try {
      const endpoint = !forceOnboarding && connectStatus?.ready
        ? '/transactions/connect/dashboard-link'
        : '/transactions/connect/session';
      const response = await api.post(endpoint, {});
      if (!response.data?.url) throw new Error(t('wallet.connectSessionError'));
      window.location.assign(response.data.url);
    } catch (err) {
      const message = getApiErrorMessage(err, t('wallet.connectSetupError'));
      setConnectError(message);
      toast.error(message);
    }
    finally { setConnectBusy(false); }
  };

  const withdraw = async () => {
    if (!canWithdraw || busy || connectBusy) return;
    setBusy(true);
    setWithdrawalError('');
    try {
      const { data } = await api.post('/transactions/withdraw', {});
      if (data.transaction?.stripe_payout_id) toast.success(t('wallet.withdrawStarted'));
      else toast.info(data.queued ? t('wallet.withdrawQueued') : t('wallet.payoutUnconfirmed'));
    } catch (err) {
      const message = getApiErrorMessage(err, t('wallet.withdrawError'));
      setWithdrawalError(message);
      toast.error(message);
    } finally { await refreshWallet(); setBusy(false); }
  };

  const retryWithdrawal = async () => {
    if (!canRetryWithdrawal || retryingWithdrawal) return;
    setRetryingWithdrawal(true);
    setWithdrawalError('');
    try {
      const { data } = await api.post('/transactions/withdraw/retry', {});
      if (data.transaction?.stripe_payout_id) toast.success(t('wallet.withdrawStarted'));
      else toast.info(data.queued ? t('wallet.withdrawQueued') : t('wallet.payoutUnconfirmed'));
    } catch (err) {
      const message = getApiErrorMessage(err, t('wallet.withdrawError'));
      setWithdrawalError(message);
      toast.error(message);
    } finally { await refreshWallet(); setRetryingWithdrawal(false); }
  };

  const retryMissionEarning = async (transaction) => {
    if (!connectStatus?.ready) {
      await configureStripePayout();
      return;
    }
    setRetryingEarningId(transaction.id);
    setWithdrawalError('');
    try {
      const { data } = await api.post(`/transactions/connect/earnings/${transaction.id}/retry`, {});
      if (data.transaction?.status === 'completed') toast.success(t('wallet.connectEarningTransferred'));
      else toast.info(t('wallet.connectEarningPending'));
    } catch (err) {
      const message = getApiErrorMessage(err, t('wallet.withdrawError'));
      setWithdrawalError(message);
      toast.error(message);
    } finally {
      await refreshWallet();
      setRetryingEarningId('');
    }
  };

  const viewReceipt = async (transactionId) => {
    // Reserve the tab during the click: mobile browsers block window.open after await.
    let receiptWindow;
    setReceiptBusy(true);
    setReceiptError('');
    try {
      receiptWindow = window.open('about:blank', '_blank');
      if (receiptWindow) receiptWindow.opener = null;
      const response = await api.get(`/transactions/${transactionId}/receipt`, { responseType: 'blob', expectsHtml: true });
      const url = window.URL.createObjectURL(response.data);
      setReceiptUrl(url);
      if (receiptWindow && !receiptWindow.closed) receiptWindow.location.replace(url);
    } catch (err) {
      if (receiptWindow && !receiptWindow.closed) receiptWindow.close();
      const message = t('wallet.receiptUnavailable');
      setReceiptError(message);
      toast.error(message);
    } finally {
      setReceiptBusy(false);
    }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('wallet.title')}</h1>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <Card className="sm:col-span-2">
          <CardContent className="p-5">
            <div className="text-sm text-muted-foreground">{t('wallet.available')}</div>
            <div className="text-4xl font-bold text-primary mt-1" data-testid="wallet-balance-amount">{formatPrice(user?.wallet_balance || 0)}</div>
            {withdrawalState !== 'none' && (
              <div role="status" className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-950" data-testid="withdrawal-reservation">
                <div className="text-sm">{withdrawalState === 'bank_pending' ? t('wallet.withdrawalProcessing') : t('wallet.reservedWithdrawal')}</div>
                <div className="mt-1 text-xl font-semibold">{formatPrice(reservedAmount)}</div>
                {withdrawalState === 'queued' && <p className="mt-1 text-xs">{canRetryWithdrawal ? t('wallet.queuedReadyToResume') : t('wallet.queuedWaitingForFunds')}</p>}
                {withdrawalState === 'awaiting_confirmation' && (
                  <>
                    <p className="mt-1 text-sm font-medium">{t('wallet.payoutUnconfirmed')}</p>
                    <p className="mt-1 text-xs">{t('wallet.reservedExplanation')}</p>
                  </>
                )}
                {withdrawalState === 'bank_pending' && <p className="mt-1 text-xs">{t('wallet.bankPendingExplanation')}</p>}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">{t('wallet.secureTopup')}</p>
            <div className="mt-4 rounded-lg border bg-secondary/40 p-3" data-testid="connect-balance-summary">
              <div className="text-xs text-muted-foreground">{t('wallet.withdrawable')}</div>
              <div className="text-xl font-semibold" data-testid="withdrawable-balance">{connectStatus ? formatPrice(withdrawableBalance) : '—'}</div>
              <div className="mt-3">
                <div className="text-xs text-muted-foreground">{t('wallet.connectPendingSettlement')}</div>
                <div className="font-semibold">{connectStatus ? formatPrice(connectStatus.connected_pending_balance || 0) : '—'}</div>
              </div>
              {connectStatus?.ready && withdrawableBalance < 1 && !connectStatus.withdrawal_pending && <p className="mt-2 text-xs text-muted-foreground">{t('wallet.noAvailablePayout')}</p>}
              {!connectStatus?.ready && <p className="mt-1 text-xs text-muted-foreground">{t('wallet.withdrawableNote')}</p>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="topup-button"><Plus size={16} className="mr-1" /> {t('wallet.topupCard')}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('wallet.topupTitle')}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>{t('wallet.amount')}</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} data-testid="topup-amount-input" /></div>
                  <p className="text-xs text-muted-foreground">{t('wallet.stripeRedirect')}</p>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>{t('wallet.cancel')}</Button>
                  <Button onClick={topup} data-testid="topup-confirm-button">{t('wallet.topup')}</Button>
                </DialogFooter>
              </DialogContent>
              </Dialog>
              <Button variant="outline" onClick={() => configureStripePayout()} disabled={connectBusy} data-testid="connect-bank-button"><Landmark size={16} className="mr-1" /> {connectBusy ? t('wallet.connectOpening') : connectStatus?.ready ? t('wallet.manageBank') : t('wallet.configure')}</Button>
              {connectStatus?.withdrawal_pending ? (
                <Button variant="secondary" onClick={retryWithdrawal} disabled={retryingWithdrawal || connectBusy || !canRetryWithdrawal} data-testid="withdraw-button">
                  {retryingWithdrawal ? t('wallet.sending') : connectStatus.withdrawal_retryable ? t('wallet.retryWithdrawal') : withdrawalState === 'queued' ? t('wallet.waitingForStripeFunds') : t('wallet.withdrawalInProgress')}
                </Button>
              ) : (
                <Button variant="secondary" onClick={withdraw} disabled={busy || connectBusy || !canWithdraw} data-testid="withdraw-button">
                  {busy ? t('wallet.sending') : t('wallet.withdraw')}
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{connectStatus?.ready ? t('wallet.stripeReady') : t('wallet.stripeSetupRequired')}</p>
            {connectError && <p role="alert" className="mt-3 text-sm text-destructive">{connectError}</p>}
            {withdrawalError && <p role="alert" className="mt-3 text-sm text-destructive" data-testid="withdrawal-error">{withdrawalError}</p>}
            {connectStatus?.connected && !connectStatus.ready && (
              <div className="mt-3 rounded-lg border bg-secondary/40 p-3 text-sm" data-testid="connect-requirements">
                <p>{connectStatus.pending_verification?.length ? t('wallet.stripeVerificationPending') : t('wallet.stripeInformationRequired')}</p>
                <ul className="mt-2 list-disc pl-5">
                  {[...new Set([...(connectStatus.currently_due || []), ...(connectStatus.past_due || [])])].map(field => (
                    <li key={field}>{t(`wallet.requirement.${field}`) === `wallet.requirement.${field}` ? t('wallet.requirement.other') : t(`wallet.requirement.${field}`)}</li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">{t('wallet.history')}</CardTitle></CardHeader>
        <CardContent>
          {receiptError && <p role="alert" className="mb-3 text-sm text-destructive">{receiptError}</p>}
          {receiptUrl && <a href={receiptUrl} target="_blank" rel="noopener noreferrer" className="mb-3 inline-flex min-h-11 items-center text-sm font-medium text-primary underline" data-testid="receipt-open-link">{t('wallet.viewReceipt')}</a>}
          {txs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">{t('wallet.noTransactions')}</div>
          ) : (
            <div className="divide-y">
              {txs.map(transaction => {
                const connectEarning = transaction.type === 'payout' && transaction.method === 'stripe_connect_earning';
                const stripeBankPayout = transaction.type === 'payout' && bankPayoutMethods.includes(transaction.method);
                const positive = ['topup', 'refund'].includes(transaction.type)
                  || (transaction.type === 'payout' && ['wallet', 'stripe_connect_earning'].includes(transaction.method));
                const title = transaction.type === 'topup'
                  ? t('wallet.topupHistoryLabel')
                  : connectEarning
                    ? transaction.status === 'pending' ? t('wallet.connectEarningPending') : t('wallet.connectEarningTransferred')
                    : stripeBankPayout ? payoutLabel(transaction) : transaction.description || transaction.type;
                return (
                  <div key={transaction.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${positive ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {positive ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
                      </div>
                      <div>
                        <div className="font-medium text-sm">{title}</div>
                        {stripeBankPayout && transaction.status === 'pending' && !transaction.stripe_payout_id && <div className="mt-1 text-xs text-muted-foreground">{t('wallet.reservedHistoryNote')}</div>}
                        {connectEarning && transaction.status === 'pending' && (
                          <div className="mt-2 space-y-1">
                            <div className="text-xs text-muted-foreground">{t('wallet.connectEarningPendingNote')}</div>
                            <Button size="sm" variant="outline" className="min-h-11" disabled={retryingEarningId === transaction.id || connectBusy} onClick={() => retryMissionEarning(transaction)} data-testid={`retry-connect-earning-${transaction.id}`}>
                              {retryingEarningId === transaction.id ? t('wallet.sending') : connectStatus?.ready ? t('wallet.retryConnectEarning') : t('wallet.configure')}
                            </Button>
                          </div>
                        )}
                        <div className="text-xs text-muted-foreground">{formatDateTime(transaction.created_at)} • {connectEarning ? 'Stripe Connect' : transaction.method}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`font-semibold ${positive ? 'text-emerald-700' : 'text-foreground'}`}>
                        {stripeBankPayout && transaction.status === 'failed' ? '' : positive ? '+' : '-'}{formatPrice(transaction.amount)}
                      </div>
                      {transaction.status === 'completed' && (
                        <Button size="sm" variant="ghost" className="min-h-11 min-w-11" disabled={receiptBusy} onClick={() => viewReceipt(transaction.id)} title={t('wallet.viewReceipt')} aria-label={t('wallet.viewReceipt')}>
                          <FileText size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardShell>
  );
}
